import { type AriaRole, type ReactNode } from 'react';
import classnames from 'classnames';
import { Icon } from 'components/common';
import { noop } from 'libs/utils';

const VARIANTS = ['danger', 'error', 'primary-light'] as const;

interface CloseButtonProps {
  onClick: () => void;
  testId?: string;
}

const CloseButton = ({ onClick, testId }: CloseButtonProps) => (
  <button
    aria-label="Close"
    className="alert--close btn-close"
    data-testid={`${testId}-close-btn`}
    onClick={onClick}
    type="button"
  >
    <Icon name="xmark" />
  </button>
);

/**
 * @param {Object} props
 * @param {Boolean} props.isDismissable Adds a close button if `true`
 * @param {Boolean} props.isImportant Since the `alert` role is intrusive,
 * only add it if important. For more details, see
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_alert_role
 */
interface AlertProps {
  children: ReactNode;
  isDismissable?: boolean;
  isImportant?: boolean;
  onClose?: () => void;
  testId?: string;
  variant?: (typeof VARIANTS)[number];
}

const Alert = ({
  children,
  isDismissable = false,
  isImportant = false,
  onClose = noop,
  testId,
  variant = 'primary-light',
}: AlertProps) => (
  <div
    className={classnames('alert', `alert-${variant}`, {
      'alert-dismissible': isDismissable,
    })}
    data-testid={testId}
    role={(isImportant ? 'alert' : null) as AriaRole | undefined}
  >
    {children}
    {isDismissable && <CloseButton onClick={onClose} testId={testId} />}
  </div>
);

export default Alert;
